/* global fetch, localStorage, console */
import { ToolBase, ToolDescription } from "../../../agents/base";
import GlobalStateManager from "../../../global-state/globalStateManager";
import { SearchFilesParamsSchema } from "./searchFiles.schema";
import { PYTHON_BACKEND_URL, getResolvedPythonBackendUrl, } from "@carousel-office/shared";
const SYSTEM_PROMPT_DESCRIPTION = `### searchFiles
Search across uploaded external files (PDF, Word, Excel, PowerPoint) indexed in RAG.

Use when you need evidence from external files, not from the active workbook.
Accepts file indexes (f0 / 0 / [0,1]) and resolves them via backend index mapping.

Search types:
- vector (default): semantic matching
- fuzzy: approximate text matching
- exact: literal phrase matching`;
const resolvePythonBackendBaseUrl = () => getResolvedPythonBackendUrl(PYTHON_BACKEND_URL).replace(/\/+$/, "");
function extractFilename(filepath) {
    const parts = filepath.split(/[/\\]/);
    return parts[parts.length - 1] || filepath;
}
function buildLocationString(result) {
    const parts = [];
    if (result.sheet_name)
        parts.push(`Sheet: ${result.sheet_name}`);
    if (result.range_address)
        parts.push(result.range_address);
    if (result.page_num) {
        let page = `Page ${result.page_num}`;
        if (result.total_pages) {
            page += ` of ${result.total_pages}`;
        }
        parts.push(page);
    }
    if (result.block_type)
        parts.push(`(${result.block_type})`);
    return parts.length ? parts.join(", ") : "Unknown location";
}
function resolveAuthHeader() {
    try {
        const token = GlobalStateManager.getInstance().getJwtToken();
        if (token && token.trim()) {
            return `Bearer ${token}`;
        }
    }
    catch {
        // Ignore auth lookup failures.
    }
    return null;
}
function parseFileIndex(value) {
    if (typeof value === "number" && Number.isFinite(value) && value >= 0) {
        return Math.floor(value);
    }
    if (typeof value !== "string")
        return null;
    const trimmed = value.trim().toLowerCase();
    if (!trimmed)
        return null;
    if (/^f\d+$/.test(trimmed)) {
        return Number.parseInt(trimmed.slice(1), 10);
    }
    if (/^\d+$/.test(trimmed)) {
        return Number.parseInt(trimmed, 10);
    }
    return null;
}
export class SearchFilesTool extends ToolBase {
    constructor() {
        super(...arguments);
        this.name = "searchFiles";
        this.description = new ToolDescription("searchFiles", "Search external RAG-indexed files using vector/fuzzy/exact matching.", SearchFilesParamsSchema);
        this.paramsSchema = SearchFilesParamsSchema;
        this.systemPromptDescription = SYSTEM_PROMPT_DESCRIPTION;
        this.fieldsToCheck = [
            { name: "uiDescription", type: "string", list_status: false, order: 0 },
            { name: "searchType", type: "string", list_status: false, order: 1 },
            { name: "searchOperator", type: "string", list_status: false, order: 2 },
            { name: "sheetName", type: "string", list_status: false, order: 3 },
            { name: "pageNumber", type: "number", list_status: false, order: 4 },
            { name: "numResults", type: "number", list_status: false, order: 5 },
        ];
        this.listsToCheck = [
            { name: "phrases", type: "array", item_type: "string", list_status: true, order: 1 },
            { name: "searchFiles", type: "array", item_type: "string", list_status: true, order: 2 },
            { name: "excludeFiles", type: "array", item_type: "number", list_status: true, order: 3 },
        ];
    }
    async execute(params) {
        const startedAt = Date.now();
        const backendBaseUrl = resolvePythonBackendBaseUrl();
        try {
            const [searchFiles, excludeFiles] = await Promise.all([
                this.resolveSearchFilesInput(params.searchFiles, backendBaseUrl),
                this.resolveExcludeFilesInput(params.excludeFiles, backendBaseUrl),
            ]);
            const requestBody = {
                phrases: params.phrases,
                search_type: params.searchType || "vector",
                num_results: params.numResults || 3,
            };
            if (params.searchOperator)
                requestBody.search_operator = params.searchOperator;
            if (searchFiles !== undefined)
                requestBody.search_files = searchFiles;
            if (excludeFiles.length)
                requestBody.exclude_files = excludeFiles;
            if (params.sheetName)
                requestBody.sheet_name = params.sheetName;
            if (params.pageNumber)
                requestBody.page_number = params.pageNumber;
            if (params.cellsOrCharts && params.cellsOrCharts !== "both") {
                requestBody.block_type = params.cellsOrCharts === "charts" ? "chart" : "cells";
            }
            if (params.ignoreIds?.length)
                requestBody.ignore_ids = params.ignoreIds;
            if (params.moreLike?.length)
                requestBody.more_like = params.moreLike;
            const endpoint = params.searchType === "fuzzy"
                ? `${backendBaseUrl}/rag/fuzzy-search`
                : `${backendBaseUrl}/rag/search`;
            const authHeader = resolveAuthHeader();
            const headers = { "Content-Type": "application/json" };
            if (authHeader) {
                headers.Authorization = authHeader;
            }
            const response = await fetch(endpoint, {
                method: "POST",
                headers,
                body: JSON.stringify(requestBody),
            });
            if (!response.ok) {
                throw new Error(`Search API error (${response.status}): ${await response.text()}`);
            }
            const pythonResponse = (await response.json());
            const fileIndexMap = await this.fetchFileIndexMap(backendBaseUrl, headers);
            const transformed = this.transformResults(pythonResponse.results, fileIndexMap);
            this.storeResultsInContext(transformed);
            return {
                success: true,
                results: transformed,
                totalFound: pythonResponse.total_results,
                searchMetadata: {
                    searchType: pythonResponse.search_type,
                    query: pythonResponse.query,
                    filesSearched: this.getFilesSearchedIndexes(params.searchFiles, transformed),
                    executionTime: Date.now() - startedAt,
                },
            };
        }
        catch (error) {
            console.warn("[searchFiles] Failed:", error);
            return {
                success: false,
                results: [],
                totalFound: 0,
                searchMetadata: {
                    searchType: params.searchType || "vector",
                    query: params.phrases.join(" "),
                    filesSearched: [],
                    executionTime: Date.now() - startedAt,
                },
            };
        }
    }
    async resolveSearchFilesInput(input, backendBaseUrl) {
        if (input === undefined)
            return undefined;
        if (input === "all" || input === "current")
            return input;
        if (!Array.isArray(input) || input.length === 0)
            return undefined;
        const resolved = await Promise.all(input.map((item) => this.resolveFileRefToPath(item, backendBaseUrl)));
        const paths = resolved.filter((entry) => typeof entry === "string" && !!entry);
        return paths.length ? paths : undefined;
    }
    async resolveExcludeFilesInput(indexes, backendBaseUrl) {
        if (!indexes?.length)
            return [];
        const resolved = await Promise.all(indexes.map((index) => this.resolveFileIndexToPath(index, backendBaseUrl)));
        return resolved.filter((entry) => typeof entry === "string" && !!entry);
    }
    async resolveFileRefToPath(value, backendBaseUrl) {
        if (typeof value === "string") {
            const lowered = value.trim().toLowerCase();
            if (lowered === "all" || lowered === "current") {
                return lowered;
            }
        }
        const parsedIndex = parseFileIndex(value);
        if (parsedIndex !== null) {
            return this.resolveFileIndexToPath(parsedIndex, backendBaseUrl);
        }
        if (typeof value === "string" && value.trim()) {
            return value.trim();
        }
        return null;
    }
    async resolveFileIndexToPath(index, backendBaseUrl) {
        try {
            const authHeader = resolveAuthHeader();
            const headers = {};
            if (authHeader)
                headers.Authorization = authHeader;
            const response = await fetch(`${backendBaseUrl}/rag/resolve-index/${index}`, {
                method: "GET",
                headers,
            });
            if (!response.ok) {
                console.warn(`[searchFiles] Failed to resolve file index ${index}: ${response.status}`);
                return null;
            }
            const payload = (await response.json());
            return typeof payload.filepath === "string" ? payload.filepath : null;
        }
        catch (error) {
            console.warn(`[searchFiles] Failed to resolve file index ${index}:`, error);
            return null;
        }
    }
    async fetchFileIndexMap(backendBaseUrl, headers) {
        try {
            const response = await fetch(`${backendBaseUrl}/rag/files`, {
                method: "GET",
                headers,
            });
            if (!response.ok)
                return new Map();
            const payload = (await response.json());
            const mapping = new Map();
            for (const file of payload.files || []) {
                if (file.filepath) {
                    mapping.set(file.filepath, file.file_index);
                }
            }
            return mapping;
        }
        catch {
            return new Map();
        }
    }
    transformResults(pythonResults, fileIndexMap) {
        return pythonResults.map((result) => {
            const filepath = result.source_file || "";
            const filename = extractFilename(filepath);
            const fileIndex = fileIndexMap.get(filepath) ?? -1;
            const citation = {
                filename,
                filepath,
                fileIndex,
                location: buildLocationString(result),
                pageNumber: result.page_num ?? undefined,
                totalPages: result.total_pages ?? undefined,
                sheetName: result.sheet_name ?? undefined,
                rangeAddress: result.range_address ?? undefined,
                blockType: result.block_type ?? undefined,
            };
            return {
                pageId: result.page_id,
                content: result.page_content,
                relevanceScore: Number(result.score || 0),
                citation,
                imageData: result.image_data ?? undefined,
            };
        });
    }
    storeResultsInContext(results) {
        if (!results.length)
            return;
        const globalState = GlobalStateManager.getInstance();
        for (const result of results) {
            if (!result.pageId)
                continue;
            globalState.addOrUpdateContext(result.pageId, {
                id: result.pageId,
                content: result.content,
                citation: result.citation,
                imageData: result.imageData,
                source: "external_file_search",
                createdAt: new Date().toISOString(),
            });
        }
    }
    getFilesSearchedIndexes(input, results) {
        if (Array.isArray(input)) {
            const indexes = input
                .map((item) => parseFileIndex(item))
                .filter((value) => value !== null);
            return Array.from(new Set(indexes));
        }
        if (input === "all" || input === undefined) {
            const indexes = results
                .map((result) => result.citation.fileIndex)
                .filter((value) => Number.isInteger(value) && value >= 0);
            return Array.from(new Set(indexes));
        }
        return [];
    }
}
export default SearchFilesTool;
